import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserProfile } from '../../../api/auth';
import { useGetScores, useDeleteScore } from '../../../api/flappy';

import { useTranslation } from 'react-i18next';

export const FlappyLeaderboard: React.FC = () => {
  const { t } = useTranslation('auth');
  const { data: scores, isLoading, isError } = useGetScores();
  const { data: user } = useUserProfile();
  const deleteScore = useDeleteScore();
  const queryClient = useQueryClient();

  const isAdmin =
    user?.email != null && user.email.toLowerCase().endsWith('@plerion.com');

  // Load Press Start 2P arcade font from Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div
      style={{ fontFamily: "'Press Start 2P', monospace" }}
      className="flex min-h-screen flex-col items-center bg-[#0a0015] px-4 py-10 text-amber-400"
    >
      <img
        src={`${process.env.PUBLIC_PATH}pixel-pleri.png`}
        alt={t('pixelPleri')}
        className="mb-4 h-32 w-32 object-contain"
      />
      <h1 className="mb-1 text-center text-lg tracking-widest">
        {t('flappyPleri')}
      </h1>
      <h2 className="mb-8 text-center text-xs tracking-widest text-white/50">
        {t('highScores')}
      </h2>
      {isLoading && (
        <p className="animate-pulse text-xs text-white/40">{t('loading')}</p>
      )}
      {isError && (
        <p className="text-xs text-red-400">
          {t('unableToLoadScoresCheckConnection')}
        </p>
      )}
      {scores && scores.length === 0 && (
        <p className="text-xs text-white/40">{t('noScoresYetBeTheFirst')}</p>
      )}
      {scores && scores.length > 0 && (
        <ol className="w-full max-w-sm space-y-3">
          {scores.map((entry, index) => {
            const isDeleteError =
              deleteScore.isError &&
              deleteScore.variables?.displayName === entry.displayName &&
              deleteScore.variables?.score === entry.score;

            return (
              <li
                key={`${entry.displayName}-${index}`}
                className="group flex items-center justify-between pb-3"
              >
                <span
                  className={`w-16 text-xs ${
                    index === 0
                      ? 'text-yellow-400'
                      : index === 1
                        ? 'text-gray-300'
                        : index === 2
                          ? 'text-amber-600'
                          : 'text-white/50'
                  }`}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="flex-1 text-center text-sm tracking-widest">
                  {entry.displayName}
                </span>
                <span className="w-16 text-right text-sm">{entry.score}</span>
                {isAdmin && (
                  <button
                    onClick={() =>
                      deleteScore.mutate(
                        { displayName: entry.displayName, score: entry.score },
                        {
                          onSuccess: () =>
                            queryClient.invalidateQueries({
                              queryKey: ['flappy-scores'],
                            }),
                        },
                      )
                    }
                    className={`ml-3 w-4 appearance-none border-0 bg-transparent p-0 text-xs opacity-0 transition-opacity duration-150 outline-none group-hover:opacity-100 ${
                      isDeleteError
                        ? 'text-red-500'
                        : 'text-white/40 hover:text-red-400'
                    }`}
                    title={t('removeFromLeaderboard')}
                    aria-label={t('removeNameFromLeaderboard', {
                      displayName: entry.displayName,
                    })}
                  >
                    ✕
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <p className="mt-12 text-xs text-white/20">
        {t('playOnThePlerionLoginScreen')}
      </p>
      <a
        href="/"
        className="mt-4 text-xs text-white/20 underline hover:text-white/50"
      >
        {t('returnToPlerion')}
      </a>
    </div>
  );
};
